import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import {
  Trash2,
  Send,
  Plus,
  Globe,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Label } from "@components/ui/label";
import { Switch } from "@components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import CustomSelect from "@components/common/CustomSelect";
import Confirmation from "@components/common/ConfirmDialog";
import { executeWorkflow } from "../createPluginApis";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import {
  EXCLUDE_KEYS,
  handlerStyle,
  unwrapJsonData,
} from "../common/commonData";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import CustomDateTimeFormatBuilder from "../CustomDateTimeFormatBuilder";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import InfoTooltip from "@components/common/InfoTooltip";
import ExpressionEditor from "../common/ExpressionEditor";
import { flattenObject } from "../utils/fieldHelpers";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

const TIME_FORMAT_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Epoch Seconds", value: "epoch_seconds" },
  { label: "Epoch Milliseconds", value: "epoch_miliseconds" },
  { label: "Local Time", value: "local_time" },
  {
    label: "Log Format with Milliseconds",
    value: "log_format_with_miliseconds",
  },
  { label: "UTC Zulu", value: "utc_zulu" },
  { label: "UTC Milliseconds", value: "utc_miliseconds" },
  { label: "UTC Offset Format", value: "utc_offset_format" },
  { label: "Date Only Format", value: "date_only" },
  { label: "Week Date Format", value: "week_date" },
  { label: "Ordinal Date Format", value: "ordinal_date" },
  { label: "Custom Format", value: "custom" },
];

const LOG_COLLECTION_PERIOD_OPTIONS = [
  { label: "Last 1 hour", value: "one_hour" },
  { label: "Last 1 day", value: "one_day" },
  { label: "Last 7 days", value: "seven_days" },
  { label: "Last 30 days", value: "thirty_days" },
];

interface RequestBlockData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const RequestBlock: React.FC<NodeProps<RequestBlockData>> = ({ id, data }) => {
  const label = data.label || data.header || "";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [paginationIn, setPaginationIn] = useState("params");
  const [filtrationOnIn, setFiltrationOnIn] = useState("params");
  const [paginationWhenCompleted, setPaginationWhenCompleted] =
    useState("responseIsEmpty");
  const [paginationMaxPages, setPaginationMaxPages] = useState(0);
  const [paginationStatusCodeExpression, setPaginationStatusCodeExpression] =
    useState("");
  const [showHeaders, setShowHeaders] = useState(false);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Request state
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("");
  const [headers, setHeaders] = useState<Array<{ key: string; value: string }>>(
    [{ key: "", value: "" }],
  );
  const [body, setBody] = useState("");
  const [bodyType, setBodyType] = useState("none");
  const [rawBodyType, setRawBodyType] = useState("none");
  const [formData, setFormData] = useState<
    Array<{ key: string; value: string; enabled: boolean }>
  >([{ key: "", value: "", enabled: true }]);

  // Auth state
  const [showAuth, setShowAuth] = useState(false);
  const [authType, setAuthType] = useState("none");
  const [authToken, setAuthToken] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Params state
  const [showParams, setShowParams] = useState(false);
  const [queryParams, setQueryParams] = useState<
    Array<{ enabled: boolean; key: string; value: string }>
  >([{ enabled: true, key: "", value: "" }]);

  // Pagination state
  const [paginationType, setPaginationType] = useState("none");

  // Offset/Limit pagination
  const [offsetKey, setOffsetKey] = useState("");
  const [offsetCustomKey, setOffsetCustomKey] = useState("");
  const [offsetValue, setOffsetValue] = useState("");
  const [limitKey, setLimitKey] = useState("");
  const [limitCustomKey, setLimitCustomKey] = useState("");
  const [limitValue, setLimitValue] = useState("");

  // Page/Size pagination
  const [pageKey, setPageKey] = useState("");
  const [pageCustomKey, setPageCustomKey] = useState("");
  const [pageValue, setPageValue] = useState("");
  const [pageSizeKey, setPageSizeKey] = useState("");
  const [pageSizeCustomKey, setPageSizeCustomKey] = useState("");
  const [pageSizeValue, setPageSizeValue] = useState("");

  // Cursor/Token/NextUrl pagination
  const [cursorKey, setCursorKey] = useState("");
  const [cursorValue, setCursorValue] = useState("");
  const [tokenKey, setTokenKey] = useState("");
  const [tokenValue, setTokenValue] = useState("");
  const [nextUrlKey, setNextUrlKey] = useState("");

  // Filtration state
  const [filterationType, setFilterationType] = useState("none");
  const [startKey, setStartKey] = useState("");
  const [endKey, setEndKey] = useState("");
  const [customDateFormat, setCustomDateFormat] = useState("");
  const [logCollectionPeriod, setLogCollectionPeriod] = useState("one_hour");
  const [customFormat, setCustomFormat] = useState<
    Array<{
      id: string;
      type: "token" | "separator";
      label: string;
      value: string;
      originalId: string;
    }>
  >([]);

  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [sslVerify, setSslVerify] = useState(true);
  const [isAuthentication, setIsAuthentication] = useState(false);

  // Options state
  const [showOptions, setShowOptions] = useState(false);
  const [timeout, setTimeout] = useState(30);
  const [fullResponse, setFullResponse] = useState(false);

  // Response state
  const [response, setResponse] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Input data from connected nodes
  const [inputData, setInputData] = useState<any[]>([]);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);
  const syncingUrlFromParamsRef = useRef(false);
  const syncingParamsFromUrlRef = useRef(false);

  const splitUrl = React.useCallback((rawUrl: string) => {
    const hashIndex = rawUrl.indexOf("#");
    const beforeHash = hashIndex >= 0 ? rawUrl.slice(0, hashIndex) : rawUrl;
    const hash = hashIndex >= 0 ? rawUrl.slice(hashIndex) : "";

    const queryIndex = beforeHash.indexOf("?");
    const base = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
    const query = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : "";

    return { base, query, hash };
  }, []);

  const resolvePluginId = React.useCallback(
    (activeMeshFlow?: any): string => {
      const pickValid = (...values: any[]): string => {
        for (const value of values) {
          if (typeof value !== "string") continue;
          const trimmed = value.trim();
          if (trimmed && trimmed !== "temp-plugin") return trimmed;
        }
        return "";
      };

      const nodeStoredParameters = (data as any)?.storedParameters || {};
      const configData = inputData?.[0]?.configData || {};

      return pickValid(
        sessionStorage.getItem("meshPluginId"),
        sessionStorage.getItem("editPluginId"),
        activeMeshFlow?.plugin_id,
        activeMeshFlow?.pluginId,
        (data as any)?.pluginId,
        (data as any)?.plugin_id,
        nodeStoredParameters?.plugin_id,
        nodeStoredParameters?.pluginId,
        configData?.pluginId,
        configData?.plugin_id,
      );
    },
    [data, inputData],
  );

  const parseQueryParamsFromUrl = React.useCallback(
    (
      rawUrl: string,
    ): Array<{ enabled: boolean; key: string; value: string }> => {
      const { query } = splitUrl(rawUrl);
      if (!query.trim()) {
        return [{ enabled: true, key: "", value: "" }];
      }

      const parsed = query
        .split("&")
        .filter((part) => part !== "")
        .map((part) => {
          const eqIndex = part.indexOf("=");
          const rawKey = eqIndex >= 0 ? part.slice(0, eqIndex) : part;
          const rawValue = eqIndex >= 0 ? part.slice(eqIndex + 1) : "";

          let key = rawKey;
          let value = rawValue;

          try {
            key = decodeURIComponent(rawKey);
          } catch {
            key = rawKey;
          }

          try {
            value = decodeURIComponent(rawValue);
          } catch {
            value = rawValue;
          }

          return {
            enabled: true,
            key,
            value,
          };
        });

      return parsed.length > 0
        ? parsed
        : [{ enabled: true, key: "", value: "" }];
    },
    [splitUrl],
  );

  const buildUrlFromQueryParams = React.useCallback(
    (
      rawUrl: string,
      params: Array<{ enabled: boolean; key: string; value: string }>,
    ) => {
      const { base, hash } = splitUrl(rawUrl);
      const activeParams = params.filter((p) => p.enabled && p.key.trim());

      const queryString = activeParams
        .map((p) => `${p.key.trim()}=${p.value ?? ""}`)
        .join("&");

      return `${base}${queryString ? `?${queryString}` : ""}${hash}`;
    },
    [splitUrl],
  );

  const hasQueryParamData = React.useCallback(
    (params: Array<{ enabled: boolean; key: string; value: string }>) =>
      params.some(
        (p) => (p.key || "").trim() !== "" || (p.value || "").trim() !== "",
      ),
    [],
  );

  const paginationUrlParams = React.useMemo(() => {
    if (paginationIn !== "params" || paginationType === "none") {
      return [] as Array<{ enabled: boolean; key: string; value: string }>;
    }

    const pushParam = (
      acc: Array<{ enabled: boolean; key: string; value: string }>,
      key?: string,
      value?: string,
    ) => {
      const trimmedKey = (key || "").trim();
      if (!trimmedKey) return;
      acc.push({ enabled: true, key: trimmedKey, value: value || "" });
    };

    const params: Array<{ enabled: boolean; key: string; value: string }> = [];

    if (paginationType === "limit_offset") {
      const effectiveOffsetKey =
        offsetKey === "custom" ? offsetCustomKey : offsetKey;
      const effectiveLimitKey =
        limitKey === "custom" ? limitCustomKey : limitKey;
      pushParam(params, effectiveOffsetKey, offsetValue);
      pushParam(params, effectiveLimitKey, limitValue);
    } else if (paginationType === "cursor_pagination") {
      const effectiveLimitKey =
        limitKey === "custom" ? limitCustomKey : limitKey;
      pushParam(params, effectiveLimitKey, limitValue);
      pushParam(params, cursorKey, cursorValue);
    } else if (paginationType === "token_pagination") {
      const effectiveLimitKey =
        limitKey === "custom" ? limitCustomKey : limitKey;
      pushParam(params, effectiveLimitKey, limitValue);
      pushParam(params, tokenKey, tokenValue);
    } else if (paginationType === "page_page_size") {
      const effectivePageKey = pageKey === "custom" ? pageCustomKey : pageKey;
      const effectivePageSizeKey =
        pageSizeKey === "custom" ? pageSizeCustomKey : pageSizeKey;
      pushParam(params, effectivePageKey, pageValue);
      pushParam(params, effectivePageSizeKey, pageSizeValue);
    } else if (paginationType === "next_url") {
      const effectiveLimitKey =
        limitKey === "custom" ? limitCustomKey : limitKey;
      pushParam(params, effectiveLimitKey, limitValue);
    }

    return params;
  }, [
    paginationIn,
    paginationType,
    offsetKey,
    offsetCustomKey,
    offsetValue,
    limitKey,
    limitCustomKey,
    limitValue,
    pageKey,
    pageCustomKey,
    pageValue,
    pageSizeKey,
    pageSizeCustomKey,
    pageSizeValue,
    cursorKey,
    cursorValue,
    tokenKey,
    tokenValue,
  ]);

  // Helper function to parse custom date format string back to format array
  const parseCustomDateFormat = (formatString: string) => {
    const result: Array<{
      id: string;
      type: "token" | "separator";
      label: string;
      value: string;
      originalId: string;
    }> = [];

    const reverseFormatMap: Record<
      string,
      { value: string; id: string; label: string }
    > = {
      YYYY: { value: "%Y", id: "YYYY", label: "YYYY" },
      YY: { value: "%y", id: "YY", label: "YY" },
      MM: { value: "%m", id: "MM", label: "MM" },
      DD: { value: "%d", id: "DD", label: "DD" },
      HH: { value: "%H", id: "HH", label: "HH" },
      hh: { value: "%I", id: "hh", label: "hh" },
      mm: { value: "%M", id: "mm", label: "mm" },
      ss: { value: "%S", id: "ss", label: "ss" },
      "AM/PM": { value: "%p", id: "A", label: "AM/PM" },
    };

    const separatorMap: Record<string, { label: string; id: string }> = {
      "/": { label: "/", id: "slash" },
      "-": { label: "-", id: "dash" },
      ":": { label: ":", id: "colon" },
      " ": { label: "Space", id: "space" },
      ".": { label: ".", id: "dot" },
      ",": { label: ",", id: "comma" },
    };

    const tokenKeys = Object.keys(reverseFormatMap).sort(
      (a, b) => b.length - a.length,
    );

    let i = 0;
    while (i < formatString.length) {
      let matched = false;

      for (const key of tokenKeys) {
        if (formatString.startsWith(key, i)) {
          const token = reverseFormatMap[key];
          result.push({
            id: `${token.id}-${Date.now()}-${i}`,
            type: "token",
            label: token.label,
            value: token.value,
            originalId: token.id,
          });
          i += key.length;
          matched = true;
          break;
        }
      }

      if (matched) continue;

      const char = formatString[i];
      if (separatorMap[char]) {
        const sep = separatorMap[char];
        result.push({
          id: `${sep.id}-${Date.now()}-${i}`,
          type: "separator",
          label: sep.label,
          value: char,
          originalId: sep.id,
        });
        i++;
        continue;
      }

      result.push({
        id: `unknown-${Date.now()}-${i}`,
        type: "separator",
        label: char,
        value: char,
        originalId: "custom",
      });
      i++;
    }

    return result;
  };

  // Copy response to clipboard
  const handleCopyResponse = () => {
    const dataToCopy = errorResponse || response;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: errorResponse
        ? "Error copied to clipboard."
        : "Response copied to clipboard.",
      duration: 2000,
    });
  };

  // Generate UID on mount if not exists
  useEffect(() => {
    if (!uid) {
      const generatedUid = Date.now().toString();
      setUid(generatedUid);
    }
  }, []);

  // Sync title from parent data (for validation rejection)
  useEffect(() => {
    if (data.title !== undefined && data.title !== title) {
      setTitle(data.title);
    }
  }, [data.title]);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || data || {};

    if (params.url) setUrl(params.url);
    if (params.method) setMethod(params.method);
    if (params.ssl_verify !== undefined) setSslVerify(params.ssl_verify);
    if (params.is_authentication !== undefined)
      setIsAuthentication(params.is_authentication);

    // Load options
    if (params.options) {
      if (params.options.timeout !== undefined)
        setTimeout(params.options.timeout);
      if (params.options.full_response !== undefined)
        setFullResponse(params.options.full_response);
    }

    // Load options
    if (params.options) {
      if (params.options.timeout !== undefined)
        setTimeout(params.options.timeout);
      if (params.options.full_response !== undefined)
        setFullResponse(params.options.full_response);
    }

    // Load headers
    if (
      params.headers &&
      Array.isArray(params.headers) &&
      params.headers.length > 0
    ) {
      setHeaders(params.headers);

      const hasHeaders = params.headers.some(
        (h: any) => h.key?.trim() || h.value?.trim(),
      );

      if (hasHeaders) {
        setShowHeaders(true);
      }
    }

    // Load auth
    if (params.auth) {
      const hasAuthData =
        params.auth.type &&
        params.auth.type !== "none" &&
        (params.auth.token || params.auth.username || params.auth.password);

      if (hasAuthData) {
        setShowAuth(true);
      }

      switch (params.auth.type) {
        case "basic_auth":
          setAuthType("basic");
          setUsername(params.auth.username || "");
          setPassword(params.auth.password || "");
          break;

        case "bearer_token":
          setAuthType("bearer");
          setAuthToken(params.auth.token || "");
          break;

        case "api_key":
          setAuthType("api_key");
          setAuthToken(params.auth.token || "");
          break;

        case "authorization":
          setAuthType("authorization");
          setAuthToken(params.auth.token || "");
          break;

        default:
          setAuthType("none");
      }
    }

    // Load query params
    if (
      params.params &&
      params.params.type === "params" &&
      params.params.parameter
    ) {
      const mappedParams = params.params.parameter.map((p: any) => ({
        enabled: true,
        key: p.key || "",
        value: p.value || "",
      }));
      setQueryParams(mappedParams);
      if (hasQueryParamData(mappedParams)) {
        setShowParams(true);
      }
    }

    // Load body
    if (params.body_type) {
      setBodyType(params.body_type);
      if (params.body_type === "raw") {
        setBody(params.body || "");
        setRawBodyType(params.raw_type || "text");
      } else if (
        (params.body_type === "x-www-form-urlencoded" ||
          params.body_type === "form-data") &&
        params.body
      ) {
        const formDataArray = Object.entries(params.body).map(
          ([key, value]) => ({
            key,
            value: value as string,
            enabled: true,
          }),
        );
        setFormData(
          formDataArray.length > 0
            ? formDataArray
            : [{ key: "", value: "", enabled: true }],
        );
      }
    }

    // Load pagination
    if (params.pagination && params.pagination.type !== "none") {
      setPaginationType(params.pagination.type);

      if (params.pagination.location) {
        setPaginationIn(params.pagination.location);
      }

      if (params.pagination.pagination_complete_when) {
        setPaginationWhenCompleted(params.pagination.pagination_complete_when);
      }
      const paginationParam = params.pagination.parameter || {};

      if (params.pagination.type === "limit_offset") {
        const offset = paginationParam.offset_key || "";
        const limit = paginationParam.limit_key || "";

        // Check if offset key is a known option
        if (["offset", "skip", "start"].includes(offset)) {
          setOffsetKey(offset);
        } else if (offset) {
          setOffsetKey("custom");
          setOffsetCustomKey(offset);
        }
        setOffsetValue(paginationParam.offset_value || "");

        // Check if limit key is a known option
        if (["limit", "count", "pageSize", "perPage"].includes(limit)) {
          setLimitKey(limit);
        } else if (limit) {
          setLimitKey("custom");
          setLimitCustomKey(limit);
        }
        setLimitValue(paginationParam.limit_value || "");
      } else if (params.pagination.type === "cursor_pagination") {
        const limit = paginationParam.limit_key || "";

        // Check if limit key is a known option
        if (limit === "limit") {
          setLimitKey(limit);
        } else if (limit) {
          setLimitKey("custom");
          setLimitCustomKey(limit);
        }
        setLimitValue(paginationParam.limit_value || "");
        setCursorKey(paginationParam.cursor_key || "");
        setCursorValue(paginationParam.cursor_value || "");
      } else if (params.pagination.type === "token_pagination") {
        const limit = paginationParam.limit_key || "";

        // Check if limit key is a known option
        if (limit === "limit") {
          setLimitKey(limit);
        } else if (limit) {
          setLimitKey("custom");
          setLimitCustomKey(limit);
        }
        setLimitValue(paginationParam.limit_value || "");
        setTokenKey(paginationParam.token_key || "");
        setTokenValue(paginationParam.token_value || "");
      } else if (params.pagination.type === "page_size_pagination") {
        setPaginationType("page_page_size");
        const page = paginationParam.page_key || "";
        const size = paginationParam.per_page_key || "";

        // Check if page key is a known option
        if (["page", "p", "pageNumber"].includes(page)) {
          setPageKey(page);
        } else if (page) {
          setPageKey("custom");
          setPageCustomKey(page);
        }
        setPageValue(paginationParam.page_value || "");

        // Check if size key is a known option
        if (["size", "pageSize", "perPage"].includes(size)) {
          setPageSizeKey(size);
        } else if (size) {
          setPageSizeKey("custom");
          setPageSizeCustomKey(size);
        }
        setPageSizeValue(paginationParam.per_page_value || "");
      } else if (params.pagination.type === "next_url_pagination") {
        setPaginationType("next_url");
        const limit = paginationParam.limit_key || "";

        // Check if limit key is a known option
        if (limit === "limit") {
          setLimitKey(limit);
        } else if (limit) {
          setLimitKey("custom");
          setLimitCustomKey(limit);
        }
        setLimitValue(paginationParam.limit_value || "");
        setNextUrlKey(paginationParam.next_url_key || "");
      }
    }

    // Load filteration
    if (params.filteration && params.filteration.type !== "none") {
      // Map backend types back to UI types
      const backendType = params.filteration.type;
      let uiFilterationType = "none";

      if (backendType === "custom_date_format") {
        uiFilterationType = "custom";
      } else {
        // All other types map directly
        uiFilterationType = backendType;
      }

      setFilterationType(uiFilterationType);
      setFiltrationOnIn(params.filteration.location || "params");
      setStartKey(params.filteration.start_key || "");
      setEndKey(params.filteration.end_key || "");
      setCustomDateFormat(params.filteration.custom_date_format || "");
      setLogCollectionPeriod(
        params.filteration.default_cron_start_time || "one_hour",
      );

      // Parse custom format if type is custom
      if (
        uiFilterationType === "custom" &&
        params.filteration.custom_date_format
      ) {
        const parsedFormat = parseCustomDateFormat(
          params.filteration.custom_date_format,
        );
        setCustomFormat(parsedFormat);
      }
    }

    // Load response if available
    if ((data as any).response) {
      setResponse(unwrapJsonData((data as any).response));
    }
  }, [hasQueryParamData]); // Only run once on mount with stable callback

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Get input data from connected nodes - use stringified comparison to avoid infinite loops
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Auto-refresh when expanded
  useEffect(() => {
    if (!isExpanded || !reactFlowInstance) return;
    const interval = setInterval(() => {
      const edges = reactFlowInstance.getEdges();
      const nodes = reactFlowInstance.getNodes();
      setInputData(getConnectedInputs(id, edges, nodes));
    }, 1000);
    return () => clearInterval(interval);
  }, [isExpanded, reactFlowInstance, id]);

  // Ref to track previous response data for sync effect
  const prevResponseDataRef = useRef<any>(null);

  // Ref to track previous stored parameters to prevent infinite loops
  const prevStoredParametersRef = useRef<any>(null);

  // Convert custom format array to string format
  useEffect(() => {
    if (filterationType === "custom" && customFormat.length > 0) {
      const formatString = customFormat
        .map((item) => {
          if (item.type === "separator") {
            return item.value;
          }
          const formatMap: Record<string, string> = {
            "%Y": "YYYY",
            "%y": "YY",
            "%m": "MM",
            "%d": "DD",
            "%H": "HH",
            "%I": "hh",
            "%M": "mm",
            "%S": "ss",
            "%p": "AM/PM",
          };
          return formatMap[item.value] || item.value;
        })
        .join("");
      setCustomDateFormat(formatString);
    } else if (filterationType !== "custom") {
      setCustomDateFormat("");
    }
  }, [customFormat, filterationType]);

  // Keep Params in sync when URL query string is edited directly.
  useEffect(() => {
    if (syncingUrlFromParamsRef.current) {
      syncingUrlFromParamsRef.current = false;
      return;
    }

    const parsedParams = parseQueryParamsFromUrl(url || "");
    if (hasQueryParamData(parsedParams)) {
      setShowParams(true);
    }
    const currentParams = queryParams || [];

    if (JSON.stringify(parsedParams) !== JSON.stringify(currentParams)) {
      syncingParamsFromUrlRef.current = true;
      setQueryParams(parsedParams);
    }
  }, [url, parseQueryParamsFromUrl, hasQueryParamData]);

  // Keep URL query string in sync when Params key/value rows are edited.
  useEffect(() => {
    if (syncingParamsFromUrlRef.current) {
      syncingParamsFromUrlRef.current = false;
      return;
    }

    const mergedParams = [...(queryParams || []), ...paginationUrlParams];
    const nextUrl = buildUrlFromQueryParams(url || "", mergedParams);
    if (nextUrl !== (url || "")) {
      syncingUrlFromParamsRef.current = true;
      setUrl(nextUrl);
    }
  }, [queryParams, paginationUrlParams, buildUrlFromQueryParams, url]);

  // Sync response from node data (updated during sequential execution)
  // Use deep comparison to prevent unnecessary updates
  useEffect(() => {
    const newResponseData = (data as any).response;

    // Skip if no new data
    if (!newResponseData) {
      // Clear response if explicitly set to null/undefined
      if (response !== null && newResponseData === null) {
        prevResponseDataRef.current = null;
        setResponse(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevResponseDataRef.current === newResponseData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newResponseData);
    const prevDataStr = prevResponseDataRef.current
      ? JSON.stringify(prevResponseDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevResponseDataRef.current = newResponseData;
      setResponse(unwrapJsonData(newResponseData));
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevResponseDataRef.current = newResponseData;
    }
  }, [(data as any).response]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Store current node's parameters whenever they change
  useEffect(() => {
    if (!data.onDataChange || !uid) return;

    // Resolve plugin ID from session, active flow context and node/config fallbacks.
    const pluginId = resolvePluginId();

    // Build headers array
    const headersArr = headers.filter((h) => h.key.trim() !== "");

    // Build auth object
    let auth: any = { type: "none", token: "", username: "", password: "" };
    if (authType === "basic") {
      auth = { type: "basic_auth", token: "", username, password };
    } else if (authType === "bearer") {
      auth = {
        type: "bearer_token",
        token: authToken,
        username: "",
        password: "",
      };
    } else if (authType === "api_key") {
      auth = { type: "api_key", token: authToken, username: "", password: "" };
    } else if (authType === "authorization") {
      auth = {
        type: "authorization",
        token: authToken,
        username: "",
        password: "",
      };
    }

    // Build body
    let formattedBody: any = null;
    let raw_type = null;
    if (bodyType === "raw") {
      formattedBody = body;
      raw_type = rawBodyType;
    } else if (
      bodyType === "x-www-form-urlencoded" ||
      bodyType === "form-data"
    ) {
      const formDataObj: any = {};
      formData
        .filter((f) => f.enabled && f.key)
        .forEach((f) => {
          formDataObj[f.key] = f.value;
        });
      formattedBody = formDataObj;
    }

    // Build query params
    const params =
      queryParams.length > 0 && queryParams.some((p) => p.enabled && p.key)
        ? {
            type: "params",
            parameter: queryParams
              .filter((p) => p.enabled && p.key)
              .map((p) => ({ key: p.key, value: p.value })),
          }
        : { type: "none", parameter: [] };

    // Build pagination
    let pagination: any = { type: "none" };
    if (paginationType !== "none") {
      if (paginationType === "limit_offset") {
        const effectiveOffsetKey =
          offsetKey === "custom" ? offsetCustomKey : offsetKey;
        const effectiveLimitKey =
          limitKey === "custom" ? limitCustomKey : limitKey;
        pagination = {
          type: "limit_offset",
          location: paginationIn,
          pagination_complete_when: paginationWhenCompleted,
          status_code: paginationMaxPages,
          expression: paginationStatusCodeExpression,
          parameter: {
            offset_key: effectiveOffsetKey,
            offset_value: offsetValue,
            limit_key: effectiveLimitKey,
            limit_value: limitValue,
          },
        };
      } else if (paginationType === "cursor_pagination") {
        const effectiveLimitKey =
          limitKey === "custom" ? limitCustomKey : limitKey;
        pagination = {
          type: "cursor_pagination",
          location: paginationIn,
          pagination_complete_when: paginationWhenCompleted,
          status_code: paginationMaxPages,
          expression: paginationStatusCodeExpression,
          parameter: {
            limit_key: effectiveLimitKey,
            limit_value: limitValue,
            cursor_key: cursorKey,
            cursor_value: cursorValue,
          },
        };
      } else if (paginationType === "token_pagination") {
        const effectiveLimitKey =
          limitKey === "custom" ? limitCustomKey : limitKey;
        pagination = {
          type: "token_pagination",
          location: paginationIn,
          pagination_complete_when: paginationWhenCompleted,
          status_code: paginationMaxPages,
          expression: paginationStatusCodeExpression,
          parameter: {
            limit_key: effectiveLimitKey,
            limit_value: limitValue,
            token_key: tokenKey,
            token_value: tokenValue,
          },
        };
      } else if (paginationType === "page_page_size") {
        const effectivePageKey = pageKey === "custom" ? pageCustomKey : pageKey;
        const effectivePageSizeKey =
          pageSizeKey === "custom" ? pageSizeCustomKey : pageSizeKey;
        pagination = {
          type: "page_size_pagination",
          location: paginationIn,
          pagination_complete_when: paginationWhenCompleted,
          status_code: paginationMaxPages,
          expression: paginationStatusCodeExpression,
          parameter: {
            page_key: effectivePageKey,
            page_value: pageValue,
            per_page_key: effectivePageSizeKey,
            per_page_value: pageSizeValue,
          },
        };
      } else if (paginationType === "next_url") {
        const effectiveLimitKey =
          limitKey === "custom" ? limitCustomKey : limitKey;
        pagination = {
          type: "next_url_pagination",
          location: paginationIn,
          pagination_complete_when: paginationWhenCompleted,
          status_code: paginationMaxPages,
          expression: paginationStatusCodeExpression,
          parameter: {
            limit_key: effectiveLimitKey,
            limit_value: limitValue,
            next_url_key: nextUrlKey,
          },
        };
      }
    }

    // Build filtration
    let filteration: any = {
      type: "none",
      default_cron_start_time: "none",
      start_key: "",
      end_key: "",
      custom_date_format: "",
    };
    if (filterationType !== "none") {
      filteration = {
        type:
          filterationType === "custom" ? "custom_date_format" : filterationType,
        location: filtrationOnIn,
        default_cron_start_time: logCollectionPeriod,
        start_key: startKey,
        end_key: endKey,
        custom_date_format: customDateFormat,
      };
    }

    // Store all parameters in node data
    const storedParameters = {
      url,
      method,
      params,
      filteration,
      headers: headersArr,
      auth,
      flag: 1,
      plugin_id: pluginId,
      ssl_verify: sslVerify,
      is_authentication: isAuthentication,
      config: inputData?.[0]?.configData
        ? {
            tabId: inputData[0].configData.tabId,
            uid: inputData[0].configData.uid,
            configType: inputData[0].configData.configType || "original",
          }
        : {},
      data: inputData?.[0]?.configData
        ? Object.fromEntries(
            Object.entries(inputData[0].configData).filter(
              ([key]) =>
                ![
                  "uid",
                  "isDecript",
                  "isDecrypted",
                  "is_decript",
                  "title",
                  "testConnectionBtn",
                  "tabId",
                  "configType",
                ].includes(key),
            ),
          )
        : {},
      body_type: bodyType,
      body: formattedBody,
      raw_type: raw_type,
      pagination,
      options: {
        timeout: timeout,
        full_response: fullResponse,
      },
    };

    // Deep comparison - include title and description to trigger updates when they change
    const fullStateStr = JSON.stringify({
      storedParameters,
      uid,
      title,
      description,
    });
    const prevStateStr = prevStoredParametersRef.current
      ? JSON.stringify(prevStoredParametersRef.current)
      : null;

    if (
      fullStateStr !== prevStateStr ||
      JSON.stringify(response) !== JSON.stringify(prevResponseDataRef.current)
    ) {
      prevStoredParametersRef.current = {
        storedParameters,
        uid,
        title,
        description,
      };
      prevResponseDataRef.current = response;
      data.onDataChange(id, {
        storedParameters,
        uid,
        title,
        description,
        response,
        outputData: response,
      });
    }
  }, [
    url,
    method,
    headers,
    authType,
    authToken,
    username,
    password,
    body,
    bodyType,
    rawBodyType,
    formData,
    queryParams,
    paginationIn,
    paginationWhenCompleted,
    paginationType,
    // Offset/Limit pagination
    offsetKey,
    offsetCustomKey,
    offsetValue,
    limitKey,
    limitCustomKey,
    limitValue,
    // Page/Size pagination
    pageKey,
    pageCustomKey,
    pageValue,
    pageSizeKey,
    pageSizeCustomKey,
    pageSizeValue,
    // Cursor pagination
    cursorKey,
    cursorValue,
    // Token pagination
    tokenKey,
    tokenValue,
    // Next URL pagination
    nextUrlKey,
    // Filteration
    filtrationOnIn,
    filterationType,
    startKey,
    endKey,
    customDateFormat,
    logCollectionPeriod,
    sslVerify,
    isAuthentication,
    timeout,
    fullResponse,
    uid,
    title,
    description,
    paginationMaxPages,
    paginationStatusCodeExpression,
    JSON.stringify(inputData), // Use stringified version to avoid reference changes
    resolvePluginId,
    id,
    response,
    // Removed 'data' from dependencies - it's recreated on every render
  ]);

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const addHeader = () => {
    setShowHeaders(true);
    setHeaders((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const updateHeader = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    setHeaders((prevHeaders) => {
      const updated = [...prevHeaders];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addFormDataRow = () => {
    setFormData([...formData, { key: "", value: "", enabled: true }]);
  };

  const removeFormDataRow = (index: number) => {
    setFormData(formData.filter((_, i) => i !== index));
  };

  const updateFormData = (
    index: number,
    field: "key" | "value" | "enabled",
    value: string | boolean,
  ) => {
    setFormData((prevFormData) => {
      const updated = [...prevFormData];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addQueryParam = () => {
    setQueryParams([...queryParams, { enabled: true, key: "", value: "" }]);
  };

  const removeQueryParam = (index: number) => {
    setQueryParams(queryParams.filter((_, i) => i !== index));
  };

  const isPaginationStatusExpressionRequired =
    paginationType !== "none" && paginationWhenCompleted === "other";
  const isPaginationStatusExpressionMissing =
    isPaginationStatusExpressionRequired &&
    !paginationStatusCodeExpression.trim();

  const updateQueryParam = (
    index: number,
    field: "enabled" | "key" | "value",
    value: boolean | string,
  ) => {
    setQueryParams((prevQueryParams) => {
      const updated = [...prevQueryParams];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Send API request
  const sendApiRequest = async () => {
    if (!url.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "URL is required",
        duration: 3000,
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the request node",
        duration: 3000,
      });
      return;
    }

    if (isPaginationStatusExpressionMissing) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description:
          "Stop on Status Code Expression is required when Pagination Complete When is set to Other.",
        duration: 3500,
      });
      return;
    }

    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");

    // Get active mesh flow from sessionStorage
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setLoading(true);
    setResponse(null);
    setErrorResponse(null);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);
      const pluginId = resolvePluginId(activeMeshFlow);

      if (!pluginId) {
        toast({
          variant: "destructive",
          title: "Plugin Not Saved",
          description: "Please save the plugin first before testing requests",
          duration: 4000,
        });
        return;
      }

      // Get all nodes and edges from React Flow
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: all nodes from start to current node in topological order
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Execute workflow ONCE with all nodes up to and including this node
      // Build nodes array - send all nodes in execution path (including config nodes)
      const workflowNodes = executableNodes.map((node: any) => {
        // Determine backend type and sub_type based on node type
        // Determine backend type and sub_type using centralized mapping
        const backendTypeInfo = getNodeBackendType(node.type || "custom");
        let nodeType = backendTypeInfo.type;
        let subType = backendTypeInfo.sub_type;

        // Override type/subtype if explicitly set in node data
        if (node.data?.sub_type) {
          subType = node.data.sub_type;
          nodeType = node.data.type || "core";
        }

        // Use centralized utility to get parameters (checks storedParameters first)
        const { parameters } = getNodeExecutionParameters(node, pluginId);

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.header || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Build connections between nodes - use all nodes in execution path
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      // Create a Set of node IDs in execution path for quick lookup
      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      const connections: any = {};

      // Build connections from all edges in execution path
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          // Check if source node is an if_else node
          const sourceNode = executableNodes.find(
            (n: any) => n.id === edge.source,
          );
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          // For if_else nodes, route to different output arrays based on handle
          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            // true handle → main[0], false handle → main[1]
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Build workflow payload for this execution (all nodes up to current)
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
        pluginId,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setResponse(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const responseData = unwrapJsonData(currentNodeResult.output_data);
            setResponse(responseData);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          // Show error toast with specific error message
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "Request executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        // Check for success at top level
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          // Mark current node as success
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Request executed successfully",
            duration: 3000,
          });
        } else {
          // Mark current node and all downstream nodes as failed
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Request execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Request Execution Error:", error);

      // Set error response for display
      setErrorResponse({
        error: error?.message || "Request execution failed",
        details:
          error?.response?.data ||
          error?.stack ||
          "No additional details available",
      });
      setResponse(null);

      // Get all edges for finding downstream nodes
      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      // Update current node and all downstream nodes to failed status
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Request execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: `Request Error`,
        description: error?.message || "Request execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract and merge input data from connected nodes
  const getDisplayData = (input: any) => {
    // Priority: response > outputData > configData
    if (input.response) {
      let data = input.response;

      // Unwrap single-element arrays
      if (Array.isArray(data) && data.length === 1) {
        data = data[0];
      }

      return data;
    }

    if (input.outputData) {
      let data = input.outputData;

      // Unwrap single-element arrays that are just wrappers
      // [{users: [...], total: 208}] → {users: [...], total: 208}
      // But keep multi-element arrays: [{...}, {...}] → [{...}, {...}]
      if (Array.isArray(data) && data.length === 1) {
        data = data[0];
      }

      return data;
    }

    // For configData, create an object
    const displayData: any = {};
    if (input.configData) {
      // Get list of encrypted fields from isDecript array
      const encryptedFields = input.configData?.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE_KEYS.includes(key))
        .forEach(([key, value]) => {
          // Mask encrypted fields with asterisks
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }

    return displayData;
  };

  // Use shared helper function from utils/fieldHelpers.ts

  // Prepare available fields for ExpressionEditor autocomplete
  const getAvailableFields = React.useMemo((): Array<{
    path: string;
    value: any;
  }> => {
    const fields: Array<{ path: string; value: any }> = [];

    if (inputData && inputData.length > 0) {
      // const firstInput = inputData[0];
      // const data = getDisplayData(firstInput);

      // // Use 'config' prefix for configuration nodes, '$json' for others
      // const prefix = firstInput.configData ? "config" : "$json";
      const dataInput = inputData.find(
        (input) =>
          !input.configData &&
          (input.outputData !== undefined || input.response !== undefined) &&
          (input.outputData !== null || input.response !== null),
      );

      // // Flatten the data structure to expose all nested fields
      // flattenObject(data, prefix, fields);
      if (dataInput) {
        const data = getDisplayData(dataInput);
        if (data && typeof data === "object" && Object.keys(data).length > 0) {
          flattenObject(data, "$json", fields);
        }
      }
    }

    return fields;
  }, [JSON.stringify(inputData)]);

  // Render input data section
  const renderInputData = () => {
    if (!inputData || inputData.length === 0) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer sources={sources} sourceCount={inputData.length} />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Globe
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-pulse"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={38}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content - 3 Sections */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                onValidationChange={setIsTitleValid}
                placeholder="Enter request title"
                className="nodrag"
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Enter request description"
                className="w-full p-3 border rounded text-xs resize-none nodrag"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
            style={{ gap: 0 }}
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={true} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Request Builder (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={true}>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-3 pr-2">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  {/* Method and URL */}
                  <div className="space-y-2 mb-4">
                    <div className="flex gap-2 items-start">
                      <select
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        className="px-2 py-1 border rounded text-xs w-24 h-9 font-semibold"
                      >
                        <option>GET</option>
                        <option>POST</option>
                        <option>PUT</option>
                        <option>DELETE</option>
                      </select>
                      <div className="flex-1 overflow-visible">
                        <ExpressionEditor
                          value={url}
                          onChange={setUrl}
                          placeholder="Type {{$ to see options: $json or $()"
                          availableFields={getAvailableFields}
                          nodes={nodes}
                          currentNodeId={id}
                          minHeight="32px"
                          maxHeight="150px"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Headers */}
                  <div className="space-y-2 border-2 rounded-md p-3 bg-muted">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowHeaders(!showHeaders)}
                    >
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold">Headers</label>
                      </div>
                      {showHeaders ? (
                        <ChevronUp
                          size={16}
                          className="text-muted-foreground"
                        />
                      ) : (
                        <ChevronDown
                          size={16}
                          className="text-muted-foreground"
                        />
                      )}
                    </div>

                    {showHeaders && (
                      <div className="space-y-2 pt-2 border-t">
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={addHeader}
                            className="h-6 text-xs px-2"
                          >
                            <Plus size={14} className="mr-1" />
                            Add Header
                          </Button>
                        </div>

                        {headers.length > 0 && (
                          <div
                            className="border-2 rounded-md p-1 border-l-4"
                            style={{ borderLeftColor: "var(--node-aggregate)" }}
                          >
                            <div className="space-y-2 cursor-default">
                              {headers.map((header, index) => (
                                <div
                                  key={index}
                                  className="flex items-start gap-2 rounded-sm p-2 border-b"
                                >
                                  {/* Key */}
                                  <div className="flex-1 min-w-0">
                                    <label className="text-xs text-muted-foreground mb-1 block">
                                      Key
                                    </label>
                                    <ExpressionEditor
                                      value={header.key}
                                      onChange={(value) =>
                                        updateHeader(index, "key", value)
                                      }
                                      placeholder="Type {{$ for options or enter key name"
                                      availableFields={getAvailableFields}
                                      nodes={nodes}
                                      currentNodeId={id}
                                      minHeight="28px"
                                      maxHeight="100px"
                                    />
                                  </div>

                                  {/* Value */}
                                  <div className="flex-1 min-w-0">
                                    <label className="text-xs text-muted-foreground mb-1 block">
                                      Value
                                    </label>
                                    <ExpressionEditor
                                      value={header.value}
                                      onChange={(value) =>
                                        updateHeader(index, "value", value)
                                      }
                                      placeholder="Type {{$ for options or enter value"
                                      availableFields={getAvailableFields}
                                      nodes={nodes}
                                      currentNodeId={id}
                                      minHeight="28px"
                                      maxHeight="100px"
                                    />
                                  </div>

                                  {/* Delete */}
                                  <div className="pt-6">
                                    <Trash2
                                      size={14}
                                      className="cursor-pointer text-primary flex-shrink-0"
                                      onClick={() => removeHeader(index)}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Authorization */}
                  <div className="space-y-2 border-2 bg-muted rounded-md p-3">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowAuth(!showAuth)}
                    >
                      <label className="text-xs font-semibold">
                        Authorization
                      </label>
                      {showAuth ? (
                        <ChevronUp
                          size={16}
                          className="text-muted-foreground"
                        />
                      ) : (
                        <ChevronDown
                          size={16}
                          className="text-muted-foreground"
                        />
                      )}
                    </div>

                    {showAuth && (
                      <div className="space-y-2 pt-2 border-t">
                        <select
                          value={authType}
                          onChange={(e) => setAuthType(e.target.value)}
                          className="w-full px-2 py-1 border rounded text-xs h-9"
                        >
                          <option value="none">No Auth</option>
                          <option value="bearer">Bearer Token</option>
                          <option value="basic">Basic Auth</option>
                          <option value="api_key">API Key</option>
                          <option value="authorization">Authorization</option>
                        </select>

                        {authType === "bearer" && (
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.add(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                              const dragData =
                                e.dataTransfer.getData("application/json");
                              if (dragData) {
                                try {
                                  const parsedData = JSON.parse(dragData);
                                  const fieldPath =
                                    parsedData.path || parsedData.value || "";
                                  if (fieldPath) {
                                    setAuthToken(
                                      (prev) => `${prev}${fieldPath}`,
                                    );
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error parsing drag data:",
                                    error,
                                  );
                                }
                              }
                            }}
                            className="w-full border border-transparent rounded transition-all"
                          >
                            <Input
                              value={authToken}
                              onChange={(e) => setAuthToken(e.target.value)}
                              placeholder="Drag field or enter token"
                              className="text-xs h-7"
                            />
                          </div>
                        )}

                        {authType === "api_key" && (
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.add(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                              const dragData =
                                e.dataTransfer.getData("application/json");
                              if (dragData) {
                                try {
                                  const parsedData = JSON.parse(dragData);
                                  const fieldPath =
                                    parsedData.path || parsedData.value || "";
                                  if (fieldPath) {
                                    setAuthToken(
                                      (prev) => `${prev}${fieldPath}`,
                                    );
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error parsing drag data:",
                                    error,
                                  );
                                }
                              }
                            }}
                            className="w-full border border-transparent rounded transition-all"
                          >
                            <Input
                              value={authToken}
                              onChange={(e) => setAuthToken(e.target.value)}
                              placeholder="Drag field or enter API Key"
                              className="text-xs h-7"
                            />
                          </div>
                        )}

                        {authType === "authorization" && (
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.add(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                              const dragData =
                                e.dataTransfer.getData("application/json");
                              if (dragData) {
                                try {
                                  const parsedData = JSON.parse(dragData);
                                  const fieldPath =
                                    parsedData.path || parsedData.value || "";
                                  if (fieldPath) {
                                    setAuthToken(
                                      (prev) => `${prev}${fieldPath}`,
                                    );
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error parsing drag data:",
                                    error,
                                  );
                                }
                              }
                            }}
                            className="w-full border border-transparent rounded transition-all"
                          >
                            <Input
                              value={authToken}
                              onChange={(e) => setAuthToken(e.target.value)}
                              placeholder="Drag field or enter authorization token"
                              className="text-xs h-7"
                            />
                          </div>
                        )}

                        {authType === "basic" && (
                          <>
                            <div
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.add(
                                  "ring-2",
                                  "ring-primary",
                                  "bg-primary/5",
                                );
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove(
                                  "ring-2",
                                  "ring-primary",
                                  "bg-primary/5",
                                );
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove(
                                  "ring-2",
                                  "ring-primary",
                                  "bg-primary/5",
                                );
                                const dragData =
                                  e.dataTransfer.getData("application/json");
                                if (dragData) {
                                  try {
                                    const parsedData = JSON.parse(dragData);
                                    const fieldPath =
                                      parsedData.path || parsedData.value || "";
                                    if (fieldPath) {
                                      setUsername(
                                        (prev) => `${prev}${fieldPath}`,
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error parsing drag data:",
                                      error,
                                    );
                                  }
                                }
                              }}
                              className="w-full border border-transparent rounded transition-all"
                            >
                              <Input
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Drag field or enter username"
                                className="text-xs h-7"
                              />
                            </div>
                            <div
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.add(
                                  "ring-2",
                                  "ring-primary",
                                  "bg-primary/5",
                                );
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove(
                                  "ring-2",
                                  "ring-primary",
                                  "bg-primary/5",
                                );
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove(
                                  "ring-2",
                                  "ring-primary",
                                  "bg-primary/5",
                                );
                                const dragData =
                                  e.dataTransfer.getData("application/json");
                                if (dragData) {
                                  try {
                                    const parsedData = JSON.parse(dragData);
                                    const fieldPath =
                                      parsedData.path || parsedData.value || "";
                                    if (fieldPath) {
                                      setPassword(
                                        (prev) => `${prev}${fieldPath}`,
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error parsing drag data:",
                                      error,
                                    );
                                  }
                                }
                              }}
                              className="w-full border border-transparent rounded transition-all"
                            >
                              <Input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Drag field or enter password"
                                type="password"
                                className="text-xs h-7"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  {method !== "GET" && method !== "DELETE" && (
                    <div className="space-y-2 border-2 bg-muted rounded-md p-1">
                      <label className="text-xs font-semibold px-2 pt-2">
                        Body
                      </label>

                      <div className="px-2 pb-2 border-t pt-2">
                        <RadioGroup
                          value={bodyType}
                          onValueChange={setBodyType}
                          className="grid grid-cols-2 gap-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="none"
                              id="body-none"
                              className="h-3 w-3"
                            />
                            <Label
                              htmlFor="body-none"
                              className="text-xs cursor-pointer"
                            >
                              None
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="raw"
                              id="body-raw"
                              className="h-3 w-3"
                            />
                            <Label
                              htmlFor="body-raw"
                              className="text-xs cursor-pointer"
                            >
                              Raw
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="x-www-form-urlencoded"
                              id="body-form"
                              className="h-3 w-3"
                            />
                            <Label
                              htmlFor="body-form"
                              className="text-xs cursor-pointer"
                            >
                              x-www-form-urlencoded
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="form-data"
                              id="body-formdata"
                              className="h-3 w-3"
                            />
                            <Label
                              htmlFor="body-formdata"
                              className="text-xs cursor-pointer"
                            >
                              form-data
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {bodyType === "raw" && (
                        <div className="border-t pt-2 px-2 pb-2 space-y-2">
                          <select
                            value={rawBodyType}
                            onChange={(e) => setRawBodyType(e.target.value)}
                            className="w-full px-2 py-1 border rounded text-xs "
                          >
                            <option value="none">None</option>
                            <option value="text">Text</option>
                            <option value="json">JSON</option>
                            <option value="xml">XML</option>
                            <option value="html">HTML</option>
                            <option value="javascript">JavaScript</option>
                          </select>
                          <div className="overflow-visible">
                            <ExpressionEditor
                              value={body}
                              onChange={setBody}
                              placeholder="Type {{$ for options or enter request body"
                              availableFields={getAvailableFields}
                              nodes={nodes}
                              currentNodeId={id}
                              minHeight="80px"
                              maxHeight="200px"
                              validationType={
                                rawBodyType === "json" ? "json" : "none"
                              }
                            />
                          </div>
                        </div>
                      )}

                      {(bodyType === "x-www-form-urlencoded" ||
                        bodyType === "form-data") && (
                        <div className="border-t-2 pt-2">
                          <div className="flex items-center justify-between px-2 pb-2">
                            <span className="text-xs font-medium">
                              Key-Value Pairs
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={addFormDataRow}
                              className="h-6 text-xs px-2"
                            >
                              <Plus size={14} className="mr-1" /> Add Body Field
                            </Button>
                          </div>
                          {formData.length > 0 && (
                            <div
                              className="border-2 rounded-md p-2 mx-2 mb-2 border-l-4"
                              style={{ borderLeftColor: "var(--node-filter)" }}
                            >
                              <div className="space-y-3 cursor-default">
                                {formData.map((item, index) => (
                                  <div
                                    key={index}
                                    className="flex gap-1 flex-col"
                                  >
                                    <div className="flex items-center justify-between pb-1">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={item.enabled}
                                          onChange={(e) =>
                                            updateFormData(
                                              index,
                                              "enabled",
                                              e.target.checked,
                                            )
                                          }
                                          className="w-3 h-3"
                                        />
                                        <label className="text-xs text-muted-foreground">
                                          Key
                                        </label>
                                      </div>
                                      <Trash2
                                        size={12}
                                        className="cursor-pointer text-primary flex-shrink-0"
                                        onClick={() => removeFormDataRow(index)}
                                      />
                                    </div>
                                    <div className="flex-1 overflow-visible">
                                      <ExpressionEditor
                                        value={item.key}
                                        onChange={(value) =>
                                          updateFormData(index, "key", value)
                                        }
                                        placeholder="Type {{$ for options or enter key name"
                                        availableFields={getAvailableFields}
                                        nodes={nodes}
                                        currentNodeId={id}
                                        minHeight="28px"
                                        maxHeight="100px"
                                      />
                                    </div>

                                    <div className="flex-1 overflow-visible">
                                      <label className="text-xs text-muted-foreground">
                                        Value
                                      </label>
                                      <ExpressionEditor
                                        value={item.value}
                                        onChange={(value) =>
                                          updateFormData(index, "value", value)
                                        }
                                        placeholder="Type {{$ for options or enter value"
                                        availableFields={getAvailableFields}
                                        nodes={nodes}
                                        currentNodeId={id}
                                        minHeight="28px"
                                        maxHeight="100px"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Params (Query, Pagination, Filtration) */}
                  <div className="space-y-2 border-2 rounded-md p-3 bg-muted">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowParams(!showParams)}
                    >
                      <label className="text-xs font-semibold">Params</label>
                      {showParams ? (
                        <ChevronUp
                          size={16}
                          className="text-muted-foreground"
                        />
                      ) : (
                        <ChevronDown
                          size={16}
                          className="text-muted-foreground"
                        />
                      )}
                    </div>

                    {showParams && (
                      <>
                        {/* Query Params */}
                        <div className="space-y-1 border-b">
                          <div className="flex !py-2 !items-center justify-between py-1">
                            <p className="text-xs font-medium  w-full">
                              Query Params
                            </p>
                            <div
                              onClick={addQueryParam}
                              className="h-5 text-xs px-2 py-1 border bg-background rounded-md flex items-center gap-1 cursor-pointer hover:bg-muted transition-colors"
                            >
                              <Plus size={10} /> Add
                            </div>
                          </div>
                          {queryParams.length > 0 && (
                            <div
                              className="border-2 rounded-md p-2 border-l-4"
                              style={{ borderLeftColor: "var(--node-sort)" }}
                            >
                              <div className="space-y-3 cursor-default">
                                {queryParams.map((param, index) => (
                                  <div
                                    key={index}
                                    className="flex gap-1 flex-col"
                                  >
                                    <div className="flex items-center justify-between pb-1">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={param.enabled}
                                          onChange={(e) =>
                                            updateQueryParam(
                                              index,
                                              "enabled",
                                              e.target.checked,
                                            )
                                          }
                                          className="w-3 h-3"
                                        />
                                        <label className="text-xs text-muted-foreground">
                                          Key
                                        </label>
                                      </div>
                                      <Trash2
                                        size={12}
                                        className="cursor-pointer text-primary flex-shrink-0"
                                        onClick={() => removeQueryParam(index)}
                                      />
                                    </div>
                                    <div className="flex-1 overflow-visible">
                                      <ExpressionEditor
                                        value={param.key}
                                        onChange={(value) =>
                                          updateQueryParam(index, "key", value)
                                        }
                                        placeholder="Type {{$ for options or enter key name"
                                        availableFields={getAvailableFields}
                                        nodes={nodes}
                                        currentNodeId={id}
                                        minHeight="28px"
                                        maxHeight="50px"
                                      />
                                    </div>

                                    <div className="flex-1 overflow-visible">
                                      <label className="text-xs text-muted-foreground">
                                        Value
                                      </label>
                                      <ExpressionEditor
                                        value={param.value}
                                        onChange={(value) =>
                                          updateQueryParam(
                                            index,
                                            "value",
                                            value,
                                          )
                                        }
                                        placeholder="Type {{$ for options or enter value"
                                        availableFields={getAvailableFields}
                                        nodes={nodes}
                                        currentNodeId={id}
                                        minHeight="28px"
                                        maxHeight="100px"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="pt-2">
                          <label className="text-xs ">
                            Select the Pagination type
                          </label>

                          {/* ── NEW: Send In dropdown ── */}
                          <div className="space-y-1">
                            <label className="text-[10px] text-muted-foreground">
                              Send In
                            </label>
                            <Select
                              value={paginationIn}
                              onValueChange={(value) => setPaginationIn(value)}
                            >
                              <SelectTrigger className="text-xs h-8">
                                <SelectValue placeholder="Select location" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="params">Params</SelectItem>
                                <SelectItem value="body">Body</SelectItem>
                                <SelectItem value="header">Header</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Pagination */}
                        <div className="space-y-1 pt-2 border-t bg-muted rounded-md p-3 border-t-[4px] border-t-primaryBlue border ">
                          <label className="text-xs font-semibold text-primaryBlue">
                            Pagination
                          </label>
                          <Select
                            value={paginationType}
                            onValueChange={(value) => setPaginationType(value)}
                          >
                            <SelectTrigger className="text-xs h-9">
                              <SelectValue placeholder="Select pagination type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="limit_offset">
                                Limit/Offset
                              </SelectItem>
                              <SelectItem value="cursor_pagination">
                                Cursor Pagination
                              </SelectItem>
                              <SelectItem value="token_pagination">
                                Token Pagination
                              </SelectItem>
                              <SelectItem value="page_page_size">
                                Page / Page Size
                              </SelectItem>
                              <SelectItem value="next_url">Next URL</SelectItem>
                            </SelectContent>
                          </Select>

                          {paginationType === "limit_offset" && (
                            <div className="space-y-2 mt-2">
                              {/* Offset Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{
                                  borderLeftColor: "var(--node-request)",
                                }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-request)" }}
                                >
                                  Offset Configuration
                                </label>
                                <div className="space-y-2">
                                  <Select
                                    value={offsetKey}
                                    onValueChange={(value) =>
                                      setOffsetKey(value)
                                    }
                                  >
                                    <SelectTrigger className="text-xs ">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="offset">
                                        offset
                                      </SelectItem>
                                      <SelectItem value="skip">skip</SelectItem>
                                      <SelectItem value="start">
                                        start
                                      </SelectItem>
                                      <SelectItem value="custom">
                                        custom
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {offsetKey === "custom" && (
                                    <Input
                                      value={offsetCustomKey}
                                      onChange={(e) =>
                                        setOffsetCustomKey(e.target.value)
                                      }
                                      placeholder="Enter custom key"
                                      className="text-xs "
                                    />
                                  )}
                                  <Input
                                    value={offsetValue}
                                    onChange={(e) =>
                                      setOffsetValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    className="text-xs "
                                  />
                                </div>
                              </div>
                              {/* Limit Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{ borderLeftColor: "var(--node-start)" }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-start)" }}
                                >
                                  Limit Configuration
                                </label>
                                <div className="space-y-2">
                                  <Select
                                    value={limitKey}
                                    onValueChange={(value) =>
                                      setLimitKey(value)
                                    }
                                  >
                                    <SelectTrigger className="text-xs ">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="limit">
                                        limit
                                      </SelectItem>
                                      <SelectItem value="count">
                                        count
                                      </SelectItem>
                                      <SelectItem value="pageSize">
                                        pageSize
                                      </SelectItem>
                                      <SelectItem value="perPage">
                                        perPage
                                      </SelectItem>
                                      <SelectItem value="custom">
                                        custom
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {limitKey === "custom" && (
                                    <Input
                                      value={limitCustomKey}
                                      onChange={(e) =>
                                        setLimitCustomKey(e.target.value)
                                      }
                                      placeholder="Enter custom key"
                                      className="text-xs"
                                    />
                                  )}
                                  <Input
                                    value={limitValue}
                                    onChange={(e) =>
                                      setLimitValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    className="text-xs"
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {paginationType === "cursor_pagination" && (
                            <div className="space-y-2 mt-2">
                              {/* Limit Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{
                                  borderLeftColor: "var(--node-aggregate)",
                                }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-aggregate)" }}
                                >
                                  Limit Configuration
                                </label>
                                <div className="space-y-2">
                                  <Select
                                    value={limitKey}
                                    onValueChange={(value) =>
                                      setLimitKey(value)
                                    }
                                  >
                                    <SelectTrigger className="text-xs">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="limit">
                                        limit
                                      </SelectItem>
                                      <SelectItem value="custom">
                                        custom
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {limitKey === "custom" && (
                                    <Input
                                      value={limitCustomKey}
                                      onChange={(e) =>
                                        setLimitCustomKey(e.target.value)
                                      }
                                      placeholder="Enter custom key"
                                      className="text-xs"
                                    />
                                  )}
                                  <Input
                                    value={limitValue}
                                    onChange={(e) =>
                                      setLimitValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    className="text-xs"
                                  />
                                </div>
                              </div>
                              {/* Cursor Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{ borderLeftColor: "var(--node-sort)" }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-sort)" }}
                                >
                                  Cursor Configuration
                                </label>
                                <div className="space-y-2">
                                  <Input
                                    value={cursorKey}
                                    onChange={(e) =>
                                      setCursorKey(e.target.value)
                                    }
                                    placeholder="Cursor Key"
                                    className="text-xs "
                                  />
                                  <ExpressionEditor
                                    value={cursorValue}
                                    onChange={(value) => setCursorValue(value)}
                                    placeholder="Response Path"
                                    className="text-xs"
                                    availableFields={getAvailableFields}
                                    nodes={nodes}
                                    currentNodeId={id}
                                    responseData={response}
                                    minHeight="28px"
                                    maxHeight="100px"
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {paginationType === "token_pagination" && (
                            <div className="space-y-2 mt-2">
                              {/* Limit Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{ borderLeftColor: "var(--node-limit)" }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-limit)" }}
                                >
                                  Limit Configuration
                                </label>
                                <div className="space-y-2">
                                  <Select
                                    value={limitKey}
                                    onValueChange={(value) =>
                                      setLimitKey(value)
                                    }
                                  >
                                    <SelectTrigger className="text-xs ">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="limit">
                                        limit
                                      </SelectItem>
                                      <SelectItem value="custom">
                                        custom
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {limitKey === "custom" && (
                                    <Input
                                      value={limitCustomKey}
                                      onChange={(e) =>
                                        setLimitCustomKey(e.target.value)
                                      }
                                      placeholder="Enter custom key"
                                      className="text-xs "
                                    />
                                  )}
                                  <Input
                                    value={limitValue}
                                    onChange={(e) =>
                                      setLimitValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    className="text-xs "
                                  />
                                </div>
                              </div>
                              {/* Token Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{
                                  borderLeftColor: "var(--node-switch)",
                                }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-switch)" }}
                                >
                                  Token Configuration
                                </label>
                                <div className="space-y-2">
                                  <Input
                                    value={tokenKey}
                                    onChange={(e) =>
                                      setTokenKey(e.target.value)
                                    }
                                    placeholder="Token Key"
                                    className="text-xs "
                                  />
                                  <ExpressionEditor
                                    value={tokenValue}
                                    onChange={(value) => setTokenValue(value)}
                                    placeholder="Token Response Path"
                                    className="text-xs"
                                    availableFields={getAvailableFields}
                                    nodes={nodes}
                                    currentNodeId={id}
                                    responseData={response}
                                    minHeight="28px"
                                    maxHeight="100px"
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {paginationType === "page_page_size" && (
                            <div className="space-y-2 mt-2">
                              {/* Page Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{ borderLeftColor: "var(--node-edit)" }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-edit)" }}
                                >
                                  Page Configuration
                                </label>
                                <div className="space-y-2">
                                  <Select
                                    value={pageKey}
                                    onValueChange={(value) => setPageKey(value)}
                                  >
                                    <SelectTrigger className="text-xs ">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="page">page</SelectItem>
                                      <SelectItem value="p">p</SelectItem>
                                      <SelectItem value="pageNumber">
                                        pageNumber
                                      </SelectItem>
                                      <SelectItem value="custom">
                                        custom
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {pageKey === "custom" && (
                                    <Input
                                      value={pageCustomKey}
                                      onChange={(e) =>
                                        setPageCustomKey(e.target.value)
                                      }
                                      placeholder="Enter custom key"
                                      className="text-xs "
                                    />
                                  )}
                                  <Input
                                    value={pageValue}
                                    onChange={(e) =>
                                      setPageValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    className="text-xs "
                                  />
                                </div>
                              </div>
                              {/* Size Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{ borderLeftColor: "var(--node-merge)" }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-merge)" }}
                                >
                                  Size Configuration
                                </label>
                                <div className="space-y-2">
                                  <Select
                                    value={pageSizeKey}
                                    onValueChange={(value) =>
                                      setPageSizeKey(value)
                                    }
                                  >
                                    <SelectTrigger className="text-xs ">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="size">size</SelectItem>
                                      <SelectItem value="pageSize">
                                        pageSize
                                      </SelectItem>
                                      <SelectItem value="perPage">
                                        perPage
                                      </SelectItem>
                                      <SelectItem value="custom">
                                        custom
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {pageSizeKey === "custom" && (
                                    <Input
                                      value={pageSizeCustomKey}
                                      onChange={(e) =>
                                        setPageSizeCustomKey(e.target.value)
                                      }
                                      placeholder="Enter custom key"
                                      className="text-xs "
                                    />
                                  )}
                                  <Input
                                    value={pageSizeValue}
                                    onChange={(e) =>
                                      setPageSizeValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    className="text-xs "
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {paginationType === "next_url" && (
                            <div className="space-y-2 mt-2">
                              {/* Limit Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{
                                  borderLeftColor: "var(--node-loop-over)",
                                }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-loop-over)" }}
                                >
                                  Limit Configuration
                                </label>
                                <div className="space-y-2">
                                  <Select
                                    value={limitKey}
                                    onValueChange={(value) =>
                                      setLimitKey(value)
                                    }
                                  >
                                    <SelectTrigger className="text-xs ">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="limit">
                                        limit
                                      </SelectItem>
                                      <SelectItem value="custom">
                                        custom
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {limitKey === "custom" && (
                                    <Input
                                      value={limitCustomKey}
                                      onChange={(e) =>
                                        setLimitCustomKey(e.target.value)
                                      }
                                      placeholder="Enter custom key"
                                      className="text-xs "
                                    />
                                  )}
                                  <Input
                                    value={limitValue}
                                    onChange={(e) =>
                                      setLimitValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    className="text-xs "
                                  />
                                </div>
                              </div>
                              {/* Next URL Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{
                                  borderLeftColor: "var(--node-ifelse)",
                                }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-ifelse)" }}
                                >
                                  Next URL Configuration
                                </label>
                                <div className="space-y-2">
                                  <ExpressionEditor
                                    value={nextUrlKey}
                                    onChange={(value) => setNextUrlKey(value)}
                                    placeholder="Next URL Key"
                                    className="text-xs"
                                    availableFields={getAvailableFields}
                                    nodes={nodes}
                                    currentNodeId={id}
                                    responseData={response}
                                    minHeight="28px"
                                    maxHeight="100px"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="space-y-1">
                            <label className="text-[10px] text-muted-foreground">
                              Pagination Complete When
                            </label>
                            <Select
                              value={paginationWhenCompleted}
                              onValueChange={(value) =>
                                setPaginationWhenCompleted(value)
                              }
                            >
                              <SelectTrigger className="text-xs h-8">
                                <SelectValue placeholder="Select location" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="responseIsEmpty">
                                  Response is Empty
                                </SelectItem>
                                <SelectItem value="receiveSpecificStatusCodes">
                                  Recieve specific status
                                </SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {paginationWhenCompleted ===
                            "receiveSpecificStatusCodes" && (
                            <div className="space-y-1 mt-1">
                              <label className="text-[10px] text-muted-foreground">
                                Max Pages (limit)
                              </label>
                              <Input
                                type="number"
                                value={paginationMaxPages}
                                onChange={(e) =>
                                  setPaginationMaxPages(Number(e.target.value))
                                }
                                placeholder="e.g. 10"
                                min="1"
                                className="text-xs h-7"
                              />
                            </div>
                          )}

                          {paginationWhenCompleted === "other" && (
                            <div className="space-y-1 mt-1">
                              <label className="text-[10px] text-muted-foreground">
                                Stop on Status Code Expression
                              </label>
                              <ExpressionEditor
                                value={paginationStatusCodeExpression}
                                onChange={(value) =>
                                  setPaginationStatusCodeExpression(value)
                                }
                                placeholder="Type {{$ for options or enter status code"
                                availableFields={getAvailableFields}
                                nodes={nodes}
                                currentNodeId={id}
                                responseData={response}
                                minHeight="28px"
                                maxHeight="100px"
                              />
                              {isPaginationStatusExpressionMissing && (
                                <p className="text-[10px] text-destructive">
                                  Status code expression is required.
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Filtration */}
                        <div className="space-y-2 pt-2 border-t bg-muted rounded-md p-3 border-t-[4px] border-t-primaryBlue border ">
                          {/* Time Format Selection */}
                          <div>
                            <div className="space-y-1 pt-2">
                              <label className="text-xs font-semibold text-primaryBlue">
                                Filtration
                              </label>

                              {/* ── NEW: Send In dropdown ── */}
                              <div className="space-y-1">
                                <label className="text-[10px] text-muted-foreground">
                                  Send In
                                </label>
                                <Select
                                  value={filtrationOnIn}
                                  onValueChange={(value) =>
                                    setFiltrationOnIn(value)
                                  }
                                >
                                  <SelectTrigger className="text-xs h-8">
                                    <SelectValue placeholder="Select location" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="params">
                                      Params
                                    </SelectItem>
                                    <SelectItem value="body">Body</SelectItem>
                                    <SelectItem value="header">
                                      Header
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium">
                              Time Format
                            </label>
                            <CustomSelect
                              data={TIME_FORMAT_OPTIONS}
                              defaultValue={filterationType}
                              onSelect={(value: string) =>
                                setFilterationType(value)
                              }
                              placeholder="Select time format"
                              multiSelect={false}
                              size="sm"
                            />
                          </div>

                          {/* Filtration Fields Section */}
                          {filterationType !== "none" && (
                            <div className="space-y-2 mt-2">
                              {/* Time Range Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{
                                  borderLeftColor: "var(--node-request)",
                                }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-request)" }}
                                >
                                  Time Range Configuration
                                </label>
                                <div className="space-y-2">
                                  <div className="grid grid-cols-2 gap-1">
                                    <div className="space-y-1">
                                      <label className="text-[10px] text-muted-foreground">
                                        Start Key
                                      </label>
                                      <Input
                                        value={startKey}
                                        onChange={(e) =>
                                          setStartKey(e.target.value)
                                        }
                                        placeholder="Enter start key"
                                        className="text-xs h-7"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <label className="text-[10px] text-muted-foreground">
                                        End Key
                                      </label>
                                      <Input
                                        value={endKey}
                                        onChange={(e) =>
                                          setEndKey(e.target.value)
                                        }
                                        placeholder="Enter end key"
                                        className="text-xs h-7"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Log Collection Period Configuration Card */}
                              <div
                                className="border rounded-md p-2 border-l-4"
                                style={{ borderLeftColor: "var(--node-start)" }}
                              >
                                <label
                                  className="text-[10px] font-semibold mb-1.5 block"
                                  style={{ color: "var(--node-start)" }}
                                >
                                  Log Collection Period
                                </label>
                                <div className="space-y-2">
                                  <CustomSelect
                                    data={LOG_COLLECTION_PERIOD_OPTIONS}
                                    defaultValue={logCollectionPeriod}
                                    onSelect={(value: string) =>
                                      setLogCollectionPeriod(value)
                                    }
                                    placeholder="Select period"
                                    multiSelect={false}
                                    size="sm"
                                  />
                                </div>
                              </div>

                              {/* Custom Format Builder */}
                              {filterationType === "custom" && (
                                <CustomDateTimeFormatBuilder
                                  customFormat={customFormat}
                                  onFormatChange={setCustomFormat}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Settings */}
                  <div className="space-y-2 border-2 rounded-md p-3 bg-muted">
                    <div
                      className="flex items-center justify-between cursor-pointer "
                      onClick={() => setShowSettings(!showSettings)}
                    >
                      <label className="text-xs font-semibold">Settings</label>
                      {showSettings ? (
                        <ChevronUp
                          size={16}
                          className="text-muted-foreground"
                        />
                      ) : (
                        <ChevronDown
                          size={16}
                          className="text-muted-foreground"
                        />
                      )}
                    </div>

                    {showSettings && (
                      <>
                        <div className="flex items-center justify-between border-t pt-2">
                          <span className="text-xs">SSL Verification</span>
                          <Switch
                            checked={sslVerify}
                            onCheckedChange={setSslVerify}
                            className="scale-75"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs">Is Authentication ?</span>
                          <Switch
                            checked={isAuthentication}
                            onCheckedChange={setIsAuthentication}
                            className="scale-75"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Options */}
                  <div className="space-y-2 border-2 rounded-md p-3 bg-muted">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowOptions(!showOptions)}
                    >
                      <label className="text-xs font-semibold">Options</label>
                      {showOptions ? (
                        <ChevronUp
                          size={16}
                          className="text-muted-foreground"
                        />
                      ) : (
                        <ChevronDown
                          size={16}
                          className="text-muted-foreground"
                        />
                      )}
                    </div>

                    {showOptions && (
                      <div className="space-y-2 pt-2 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-xs">Full Response</span>
                          <Switch
                            checked={fullResponse}
                            onCheckedChange={setFullResponse}
                            className="scale-75"
                          />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <label className="text-xs">Timeout</label>
                            <InfoTooltip text="Request timeout in seconds" />
                          </div>
                          <Input
                            type="number"
                            value={timeout}
                            onChange={(e) => setTimeout(Number(e.target.value))}
                            placeholder="30"
                            min="1"
                            className="text-xs h-8"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="sticky -bottom-1 left-0 right-0 bg-background border-t">
                  <Button
                    onClick={sendApiRequest}
                    disabled={
                      !url.trim() ||
                      !method.trim() ||
                      !isTitleValid ||
                      loading ||
                      isPaginationStatusExpressionMissing
                    }
                    className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                    size="sm"
                  >
                    {loading ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send size={14} className="mr-2" />
                        Send Request
                      </>
                    )}
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || response}
                  loading={loading}
                  onCopy={handleCopyResponse}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setResponse(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}

      {/* Handles */}
      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
      />

      <Handle
        id="output"
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />
    </FullscreenWrapper>
  );
};

export default RequestBlock;
